import React, { useState, useEffect } from 'react';
import { FaShare, FaCopy, FaCheck, FaTimes, FaExternalLinkAlt, FaInfoCircle } from 'react-icons/fa';
import { generateShareUrl, getProjectSummary } from '../utils/shareUtils';

function ShareLayoutModal({ idea, components, onClose }) {
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [projectSummary, setProjectSummary] = useState(null);

  useEffect(() => {
    generateUrl();
  }, [idea, components]);

  const generateUrl = async () => {
    try {
      setIsGenerating(true);
      setError('');

      if (!idea || !components) {
        throw new Error('Missing project data');
      }

      const projectData = {
        title: idea.title,
        description: idea.description,
        icon: idea.icon,
        tags: idea.tags || [],
        components: components.map(component => ({
          id: component.id,
          type: component.type,
          title: component.title,
          data: component.data,
          position: component.position
        }))
      };

      const url = generateShareUrl(projectData);
      const summary = getProjectSummary(projectData);
      
      setShareUrl(url);
      setProjectSummary(summary);
    } catch (err) {
      console.error('Error generating share URL:', err);
      setError('Failed to generate shareable URL. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleOpenPreview = () => {
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[var(--secondary)]/10 rounded-full flex items-center justify-center">
                <FaShare className="text-[var(--secondary)] text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Share Project Layout</h3>
                <p className="text-sm text-[var(--text-secondary)]">Create a shareable link with all project data</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-lg hover:bg-[var(--bg-hover)]"
            >
              <FaTimes />
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
            <div className="flex items-start gap-3">
              <FaInfoCircle className="text-[var(--secondary)] mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-[var(--text-primary)] mb-1">How Layout Sharing Works</h4>
                <p className="text-sm text-[var(--text-secondary)] mb-2">
                  This creates a special URL containing all your project data. When someone opens it, they can:
                </p>
                <ul className="text-sm text-[var(--text-secondary)] space-y-1">
                  <li>• <strong>View Only:</strong> Browse the project without saving anything</li>
                  <li>• <strong>Import:</strong> Add the project to their own account (if logged in)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Project Summary */}
          {projectSummary && (
            <div className="mb-6 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
              <h4 className="font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
                {projectSummary.icon} {projectSummary.title}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[var(--text-secondary)]">Components:</span>
                  <span className="ml-2 font-medium text-[var(--text-primary)]">
                    {projectSummary.totalComponents}
                  </span>
                </div>
                <div>
                  <span className="text-[var(--text-secondary)]">Types:</span>
                  <span className="ml-2 font-medium text-[var(--text-primary)]">
                    {projectSummary.componentTypes}
                  </span>
                </div>
              </div>
              {projectSummary.tags?.length > 0 && (
                <div className="mt-3">
                  <span className="text-[var(--text-secondary)] text-sm">Tags:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {projectSummary.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-[var(--primary)]/10 text-[var(--primary)] rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Share URL */}
          {isGenerating ? (
            <div className="mb-6 p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] animate-pulse">
              <div className="h-4 bg-[var(--bg-hover)] rounded mb-2"></div>
              <div className="h-4 bg-[var(--bg-hover)] rounded w-3/4"></div>
            </div>
          ) : shareUrl ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Shareable Link
              </label>
              <div className="flex gap-2">
                <div className="flex-1 p-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
                  <p className="text-sm text-[var(--text-primary)] break-all">
                    {shareUrl}
                  </p>
                </div>
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 hover:scale-105 active:scale-95 ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-[var(--secondary)] text-white hover:opacity-90'
                  }`}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-lg hover:bg-[var(--bg-hover)]"
            >
              Close
            </button>
            {shareUrl && (
              <button
                onClick={handleOpenPreview}
                className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2"
              >
                <FaExternalLinkAlt />
                Preview
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareLayoutModal;
